import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Check, Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const PricingPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Check for payment verification on redirect
  useEffect(() => {
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (reference && token) {
      verifyPayment(reference);
    }
  }, [searchParams, token]);

  const verifyPayment = async (reference) => {
    setVerifying(true);
    try {
      const response = await axios.get(`${API}/subscriptions/verify/${reference}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === "success") {
        toast.success(`Upgraded to ${response.data.plan} plan!`);
        await refreshUser();
        navigate("/dashboard");
      } else {
        toast.error("Payment verification failed");
      }
    } catch (error) {
      toast.error("Failed to verify payment");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/register");
      return;
    }
    
    if (planId === "free") {
      navigate("/dashboard");
      return;
    }
    
    setLoading(planId);
    try {
      const response = await axios.post(`${API}/subscriptions/initialize`, {
        plan_id: planId,
        payment_type: "subscription"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Paystack
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize payment");
      setLoading(null);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      price: "₦0",
      period: "/month",
      description: "Perfect for getting started",
      features: [
        "Up to 2 assets",
        "Basic dashboard",
        "Manual valuations",
        "Email alerts"
      ],
      highlighted: false,
      buttonText: user?.subscription_plan === "free" ? "Current Plan" : "Get Started"
    },
    {
      id: "professional",
      name: "Professional",
      price: "₦15,000",
      period: "/month",
      description: "For serious property owners",
      features: [
        "Up to 20 assets",
        "AI-assisted appraisal",
        "Full document tracking",
        "Lease alerts (email + in-app)",
        "Marketplace access"
      ],
      highlighted: true,
      buttonText: user?.subscription_plan === "professional" ? "Current Plan" : "Upgrade"
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "₦50,000",
      period: "/month",
      description: "For large portfolios and estates",
      features: [
        "Unlimited assets",
        "Portfolio analytics",
        "Multi-user roles",
        "Priority support",
        "Custom reports"
      ],
      highlighted: false,
      buttonText: user?.subscription_plan === "enterprise" ? "Current Plan" : "Upgrade"
    }
  ];

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay absolute inset-0 pointer-events-none"></div>
      
      {/* Hero Glow */}
      <div className="hero-glow absolute inset-0 pointer-events-none"></div>

      {/* Navigation */}
      <nav className="relative z-10 glass sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Blue Element</span>
            </Link>
            
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-slate-400 hover:text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-slate-400 hover:text-white transition-colors">
                    Login
                  </Link>
                  <Link to="/register">
                    <Button className="btn-primary">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="relative z-10 py-24" data-testid="pricing-page">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Choose the plan that fits your portfolio. Upgrade anytime as you grow.
            </p>
            
            {user && (
              <div className="mt-6">
                <Badge className="bg-primary/10 text-primary border-primary/30 text-sm px-4 py-2">
                  Current Plan: {user.subscription_plan?.charAt(0).toUpperCase() + user.subscription_plan?.slice(1)}
                </Badge>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => {
              const isCurrentPlan = user?.subscription_plan === plan.id;
              const isDowngrade = user && 
                ((user.subscription_plan === "enterprise" && plan.id !== "enterprise") ||
                 (user.subscription_plan === "professional" && plan.id === "free"));
              
              return (
                <div 
                  key={plan.id}
                  className={`relative rounded-3xl p-8 ${
                    plan.highlighted 
                      ? 'bg-primary/10 border-2 border-primary glow' 
                      : 'bg-slate-900/50 border border-slate-800'
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary rounded-full text-sm font-medium text-white">
                      Most Popular
                    </div>
                  )}
                  
                  <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                  
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={`${plan.id}-feature-${idx}`} className="flex items-center gap-3 text-slate-300">
                        <Check className="w-5 h-5 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${
                      isCurrentPlan 
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed' 
                        : plan.highlighted 
                        ? 'btn-primary' 
                        : 'btn-secondary'
                    }`}
                    disabled={isCurrentPlan || isDowngrade || loading === plan.id}
                    onClick={() => handleSubscribe(plan.id)}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {loading === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : isDowngrade ? (
                      "Contact Support"
                    ) : (
                      plan.buttonText
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* FAQ or Additional Info */}
          <div className="mt-16 text-center">
            <p className="text-slate-400">
              All plans include secure payment processing via Paystack.{" "}
              <br className="hidden sm:block" />
              Need help choosing? <a href="#" className="text-primary hover:underline">Contact our team</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
